import { useOptionalEntityOverTimeContext } from '@internal/backstage-plugin-common-library-react';
import BarChartIcon from '@mui/icons-material/BarChart';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { LineChart } from '@mui/x-charts';
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Typography,
  TextField,
  makeStyles,
  Tabs,
  Tab,
} from '@material-ui/core';

import { ScorecardItem } from '../types';
import { useSelectedCategoriesParam } from '../utils';

const PROGRESS_TITLE_BY_AGGREGATION: Record<string, string> = {
  day: 'Scorecards Daily Progress',
  week: 'Scorecards Weekly Progress',
  month: 'Scorecards Monthly Progress',
  quarter: 'Scorecards Quarterly Progress',
};

interface ScorecardProgressProps {
  scorecardItems: ScorecardItem[] | null;
  flatScorecardItems?: ScorecardItem[] | null;
  actions?: React.ReactNode;
}

const VIEW_MODE_PARAM = 'progressView';

const useStyles = makeStyles(theme => ({
  mainContainer: {
    padding: 16,
    paddingBottom: 8,
    minHeight: 300,
  },
  headerSection: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: 16,
    paddingBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerTabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      minWidth: 100,
      fontSize: '0.875rem',
      padding: '6px 16px',
    },
    '& .MuiTabs-indicator': {
      height: 2,
    },
  },
  contentWrapper: {
    display: 'flex',
    gap: 16,
    minHeight: 300,
  },
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 250,
    maxWidth: 350,
    flex: '0 0 300px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '0 0 auto',
  },
  searchField: {
    flex: 1,
  },
  selectAllCheckbox: {
    padding: 4,
    flexShrink: 0,
  },
  listWrapper: {
    flex: 1,
    minHeight: 200,
    maxHeight: 400,
    overflow: 'hidden',
  },
  list: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    paddingRight: 8,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[200],
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.grey[500],
      },
    },
  },
  listItemIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  colorBox: {
    width: 10,
    height: 10,
    borderRadius: '2px',
    flexShrink: 0,
  },
  chartWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 300,
    position: 'relative',
  },
}));

const getChartSeries = (
  scorecardItems: ScorecardItem[],
  checked: string[],
  hoveredLabel: string | null,
) => {
  return scorecardItems
    .filter(item => checked.includes(item.label))
    .map(item => {
      const isHovered = hoveredLabel === item.label;
      return {
        type: 'line' as const,
        data: item.valueOverTime.map(vot => {
          if (
            vot.value === null ||
            vot.value === undefined ||
            vot.value === -1 ||
            vot.value === 'N/A'
          ) {
            return null;
          }
          return typeof vot.value === 'number'
            ? vot.value
            : Number.parseFloat(vot.value);
        }),
        label: item.label,
        showMark: false,
        connectNulls: false,
        color: hoveredLabel && !isHovered ? '#f7f4f4ff' : item.color,
        curve: 'linear' as const,
        valueFormatter: (value: number | null) =>
          value === null ? 'N/A' : `${value.toFixed(1)}%`,
      };
    });
};

export const ScorecardProgress: React.FC<ScorecardProgressProps> = ({
  scorecardItems,
  flatScorecardItems,
  actions,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [hoveredLabel, setHoveredLabel] = React.useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const classes = useStyles();

  const timeContext = useOptionalEntityOverTimeContext();
  const progressTitle =
    PROGRESS_TITLE_BY_AGGREGATION[timeContext?.aggregateBy ?? 'month'] ??
    'Scorecards Progress';

  const viewMode =
    (searchParams.get(VIEW_MODE_PARAM) as 'grouped' | 'flat') || 'flat';

  const handleViewModeChange = useCallback(
    (_: React.ChangeEvent<{}>, newValue: 'grouped' | 'flat') => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(VIEW_MODE_PARAM, newValue);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const displayItems = React.useMemo(() => {
    if (viewMode === 'flat' && flatScorecardItems) {
      return flatScorecardItems;
    }
    return scorecardItems || [];
  }, [scorecardItems, flatScorecardItems, viewMode]);

  const [selectedFromUrl, setSelectedCategories] = useSelectedCategoriesParam();

  // Server-provided defaults for the current view (used only as a seed when
  // the `categories` URL param is absent).
  const defaultChecked = React.useMemo(
    () => displayItems.filter(item => item.checked).map(item => item.label),
    [displayItems],
  );

  // URL wins; fall back to server defaults only when the param is absent (null).
  const checked = selectedFromUrl ?? defaultChecked;
  const setChecked = setSelectedCategories;

  if (scorecardItems === null || scorecardItems.length === 0) {
    return (
      <Box className={classes.mainContainer} style={{ minHeight: 80 }}>
        No categories found for this team
      </Box>
    );
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredItems = displayItems.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    const allFilteredLabels = filteredItems.map(item => item.label);
    const allFilteredChecked = allFilteredLabels.every(label =>
      checked.includes(label),
    );

    if (allFilteredChecked) {
      setChecked(checked.filter(label => !allFilteredLabels.includes(label)));
    } else {
      const newChecked = [...checked];
      allFilteredLabels.forEach(label => {
        if (!newChecked.includes(label)) {
          newChecked.push(label);
        }
      });
      setChecked(newChecked);
    }
  };

  const isAllFilteredChecked =
    filteredItems.length > 0 &&
    filteredItems.every(item => checked.includes(item.label));

  const isSomeFilteredChecked =
    filteredItems.some(item => checked.includes(item.label)) &&
    !isAllFilteredChecked;

  const chartSeries = getChartSeries(displayItems, checked, hoveredLabel);

  const labels = displayItems.at(0)?.valueOverTime.map(vot => vot.date);

  const chartHeight = Math.max(
    250,
    Math.min(450, displayItems.length * 15 + 150),
  );

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.headerSection}>
        <Box className={classes.headerLeft}>
          <BarChartIcon />
          <Typography variant="h6">{progressTitle}</Typography>

          <Box className={classes.headerTabs}>
            <Tabs
              value={viewMode}
              onChange={handleViewModeChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Flat" value="flat" />
              <Tab label="Grouped" value="grouped" />
            </Tabs>
          </Box>
        </Box>

        {actions && <Box display="flex">{actions}</Box>}
      </Box>

      <Box className={classes.contentWrapper}>
        <Box className={classes.leftPanel}>
          <Box className={classes.searchContainer}>
            <Checkbox
              edge="start"
              checked={isAllFilteredChecked}
              indeterminate={isSomeFilteredChecked}
              onChange={handleSelectAll}
              className={classes.selectAllCheckbox}
              inputProps={{ 'aria-label': 'select all categories' }}
            />
            <TextField
              label="Search..."
              variant="outlined"
              size="small"
              className={classes.searchField}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </Box>

          <Box className={classes.listWrapper}>
            <List className={classes.list}>
              {filteredItems.map(item => {
                const labelId = `checkbox-list-label-${item.label}`;
                const isHovered = hoveredLabel === item.label;

                return (
                  <ListItem key={item.label} disablePadding sx={{ py: 0.25 }}>
                    <ListItemButton
                      role={undefined}
                      onClick={handleToggle(item.label)}
                      onMouseEnter={() => setHoveredLabel(item.label)}
                      onMouseLeave={() => setHoveredLabel(null)}
                      dense
                      sx={{
                        py: 0.25,
                        minHeight: 28,
                        backgroundColor: isHovered
                          ? 'action.hover'
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon
                        className={classes.listItemIcon}
                        sx={{ minWidth: 'auto', mr: 1 }}
                      >
                        <Checkbox
                          edge="start"
                          checked={checked.includes(item.label)}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{ p: 0.5 }}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                        <Box
                          className={classes.colorBox}
                          sx={{
                            backgroundColor: item.color,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        primary={item.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>

        <Box className={classes.chartWrapper} style={{ height: chartHeight }}>
          <LineChart
            series={chartSeries}
            xAxis={[
              {
                scaleType: 'point',
                data: labels,
                tickLabelStyle: {
                  fontSize: 11,
                },
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: 100,
                tickInterval: [0, 25, 50, 75, 100],
                valueFormatter: (value: number) => `${value}%`,
              },
            ]}
            slotProps={{ legend: { hidden: true } }}
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
};
